import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Page, Panel, Field } from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import { api, notifyErr, fmtBytes } from "../lib/api";
import { useCustomers, useSelectedCustomer } from "../lib/hooks";

export default function Caricamento() {
  const nav = useNavigate();
  const { customers } = useCustomers();
  const [cid, setCid] = useSelectedCustomer(customers);
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState({ campaign: "", disposition: "", agent: "", phone_number: "", lead_id: "", call_date: "", channel_map: "none" });
  const [auto, setAuto] = useState(true);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const m = (k) => (e) => setMeta({ ...meta, [k]: e.target.value });
  const submit = async () => {
    const fd = new FormData();
    fd.append("customer_id", cid);
    files.forEach((f) => fd.append("files", f));
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
    fd.append("auto_analyze", auto);
    setBusy(true);
    try {
      const r = (await api.post("/recordings/upload", fd)).data;
      setOut(r);
      const ok = r.results.filter((x) => x.ok).length;
      toast.success(`${ok} file caricati${r.job ? " · analisi AI avviata" : ""}`);
      setFiles([]);
    } catch (e) { notifyErr(e); } finally { setBusy(false); }
  };
  return (
    <Page title="Caricamento manuale" subtitle="Importa registrazioni reali (wav, mp3, gsm, ogg, opus, flac, m4a). I metadati vengono applicati a tutti i file del caricamento." testid="caricamento-page"
      actions={<CustomerSelect customers={customers} value={cid} onChange={setCid} />}>
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="File audio">
          <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-sm p-10 cursor-pointer hover:border-[#0047FF] transition-colors" data-testid="upload-dropzone">
            <UploadCloud size={28} className="text-neutral-400" />
            <span className="text-sm mt-2">Seleziona uno o più file</span>
            <input type="file" multiple accept=".wav,.mp3,.gsm,.ogg,.opus,.flac,.m4a" className="hidden" onChange={(e) => setFiles([...e.target.files])} data-testid="upload-file-input" />
          </label>
          <ul className="mt-3 text-xs space-y-1" data-testid="upload-file-list">
            {files.map((f) => <li key={f.name} className="flex justify-between"><span>{f.name}</span><span className="text-neutral-500">{fmtBytes(f.size)}</span></li>)}
          </ul>
        </Panel>
        <Panel title="Metadati chiamata (opzionali)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefono"><Input value={meta.phone_number} onChange={m("phone_number")} className="h-8 rounded-sm" data-testid="upload-phone-input" /></Field>
            <Field label="Lead ID"><Input value={meta.lead_id} onChange={m("lead_id")} className="h-8 rounded-sm" data-testid="upload-lead-input" /></Field>
            <Field label="Campagna"><Input value={meta.campaign} onChange={m("campaign")} className="h-8 rounded-sm" data-testid="upload-campaign-input" /></Field>
            <Field label="Esito originale (es. NI)"><Input value={meta.disposition} onChange={m("disposition")} className="h-8 rounded-sm font-mono uppercase" data-testid="upload-disposition-input" /></Field>
            <Field label="Agente"><Input value={meta.agent} onChange={m("agent")} className="h-8 rounded-sm" data-testid="upload-agent-input" /></Field>
            <Field label="Data chiamata" hint="Vuoto = ora del caricamento"><Input type="datetime-local" value={meta.call_date} onChange={m("call_date")} className="h-8 rounded-sm" data-testid="upload-date-input" /></Field>
            <Field label="Canali audio">
              <Select value={meta.channel_map} onValueChange={(v) => setMeta({ ...meta, channel_map: v })}>
                <SelectTrigger className="h-8 rounded-sm" data-testid="upload-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Automatico</SelectItem><SelectItem value="left_agent">Stereo: SX agente, DX cliente</SelectItem>
                  <SelectItem value="left_customer">Stereo: SX cliente, DX agente</SelectItem><SelectItem value="mono">Forza mono</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <label className="flex items-center gap-2 text-sm pt-5"><Switch checked={auto} onCheckedChange={setAuto} data-testid="upload-auto-analyze-switch" />Avvia analisi AI</label>
          </div>
          <Button className="w-full mt-4 rounded-sm bg-[#0047FF] h-9" disabled={!cid || !files.length || busy} onClick={submit} data-testid="upload-submit-btn">{busy ? "Caricamento…" : `Carica ${files.length || ""} file`}</Button>
        </Panel>
      </div>
      {out && (
        <Panel title="Esito caricamento" className="mt-4" testid="upload-results">
          <ul className="text-sm space-y-1">
            {out.results.map((r, i) => <li key={i} className={r.ok ? "text-green-700" : "text-red-700"}>{r.file}: {r.ok ? `caricato (${r.audio.channels} canali, ${Math.round(r.audio.duration)}s, ${r.audio.codec})` : r.error}</li>)}
          </ul>
          {out.job && <Button variant="outline" className="mt-3 rounded-sm h-8" onClick={() => nav("/elaborazioni")} data-testid="go-to-jobs-btn">Segui l'elaborazione →</Button>}
        </Panel>
      )}
    </Page>
  );
}
